// SegmentedControl — the labelled tab strip (DESIGN-SYSTEM.md §1).
//
// The text-label sibling of {@link TabHandle}: a small inline group of mutually
// exclusive TEXT choices — diff Split/Overlay/Processes/Documents, a package
// picker. Where a rail of icon handles navigates, a segmented control switches
// the mode of what you're already looking at.
//
// It existed three times before this (app-shell DiffBoard's SEG_* consts,
// Studio's `.diff-segmented` CSS, DiffModeTabStrip) with byte-identical values
// expressed in two different systems. Values below are those, unchanged — the
// only thing added is the focus ring they all lacked.
import type { ReactNode } from "react";
import { TabHandleStrip } from "./TabHandleStrip.js";
import { cx } from "./classNames.js";

export interface SegmentedItem<Id extends string = string> {
  readonly id: Id;
  readonly label: ReactNode;
  /** Optional trailing node — e.g. a `Badge` count. */
  readonly trailing?: ReactNode;
  readonly title?: string;
  readonly disabled?: boolean;
}

export interface SegmentedControlProps<Id extends string = string> {
  /** Accessible name for the group. */
  readonly label: string;
  readonly items: readonly SegmentedItem<Id>[];
  readonly active: Id;
  readonly onSelect: (id: Id) => void;
  readonly className?: string;
  /** Per-item extra props hook (e.g. `data-mode`) keyed by id. */
  readonly itemProps?: (id: Id) => Record<string, unknown> | undefined;
}

const ITEM =
  "px-3 py-1 rounded-[5px] text-xs cursor-pointer transition-colors duration-[120ms] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
  "disabled:cursor-not-allowed disabled:opacity-50";
const ACTIVE = "bg-sky-400/[0.18] text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]";
const IDLE = "text-slate-400 hover:text-slate-200";

export function SegmentedControl<Id extends string = string>({
  label,
  items,
  active,
  onSelect,
  className,
  itemProps,
}: SegmentedControlProps<Id>) {
  return (
    <TabHandleStrip
      label={label}
      className={cx(
        "inline-flex rounded-[7px] border border-slate-400/15 bg-slate-400/[0.08] p-0.5",
        className,
      )}
    >
      {items.map((item) => {
        const isActive = item.id === active;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled}
            title={item.title}
            onClick={() => onSelect(item.id)}
            className={cx(
              ITEM,
              isActive ? ACTIVE : IDLE,
              item.trailing ? "inline-flex items-center gap-1.5" : null,
            )}
            {...(itemProps?.(item.id) ?? {})}
          >
            {item.label}
            {item.trailing}
          </button>
        );
      })}
    </TabHandleStrip>
  );
}
